import React, { useCallback, useEffect, useMemo, useState } from "react";
import { importacionOperacionesApi as api } from "../importacion-operaciones/importacionOperacionesApi";
import { formatearImporte, formatearFecha } from "./MotorDePago";
import "./cuota-a-cuota.css";

// Simulacion: hoy Fiserv liquida 1/N..N/N juntas. Se dice en pantalla para que nadie lea un
// COMPLETA de hoy como validacion del esquema futuro.
const NOTA_SIMULACION = "Simulación · no persiste · no genera pagos";

// Falta de datos y problema real se muestran separados: 1524 cuotas sin Clover del periodo no
// son 1524 huerfanas.
export const TEXTOS = {
  SIN_COBERTURA: "No existe información Clover suficiente del período para buscar la operación madre.",
  HUERFANA: "Fiserv informó una cuota pero, existiendo cobertura Clover, no se encontró la venta original.",
  SIN_RECUPERO: "Existe la venta Clover financiada pero no aparece ninguna cuota en los archivos Fiserv disponibles.",
  FUERA_DE_ALCANCE: "Esta financiación existe en Clover pero su recupero no se procesa por Fiserv.",
  ZIP_FALTANTE: "El cobro esperado cae en un ZIP Fiserv que no está disponible. Se reevalúa al cargar nuevos ZIP.",
};

export const FUENTE_RECUPERO = {
  FISERV: "Fiserv",
  NON_FISERV: "No Fiserv",
  UNKNOWN: "Desconocida",
};

// El color comunica que hacer con la operacion: verde = cerrada, azul = esperar,
// naranja = mirar, rojo = frenar.
const ESTADOS = {
  COMPLETE: { etiqueta: "Completa", tono: "verde" },
  IN_RECOVERY: { etiqueta: "En recupero", tono: "azul" },
  CREATED: { etiqueta: "Sin recupero Fiserv", tono: "naranja" },
  // No es un error: neutro, nunca rojo.
  OUT_OF_SCOPE_RECOVERY_SOURCE: { etiqueta: "Fuera de alcance Fiserv", tono: "neutro" },
  REVIEW: { etiqueta: "Revisar", tono: "naranja" },
  COUNT_MISMATCH: { etiqueta: "Cuotas no coinciden", tono: "rojo" },
  AMOUNT_DIFFERENCE: { etiqueta: "Diferencia de importe", tono: "rojo" },
  BLOCKED_MISSING_RAW_DETAIL: { etiqueta: "Bloqueada · sin detalle v3", tono: "naranja" },
  BLOCKED_MISSING_INSTALLMENT_EVIDENCE: { etiqueta: "Bloqueada · sin evidencia de cuotas", tono: "naranja" },
};

export const CLASIFICACION_SIN_RECUPERO = {
  POSSIBLE_MISSING_ZIP: "Posible ZIP faltante",
  SOURCE_GAP: "Falta la liquidación de esa marca",
  NOT_IN_AVAILABLE_FISERV: "No está en los ZIP disponibles",
  POSSIBLE_WEAK_MATCH: "Coincidencia débil",
  FOUND_OUTSIDE_EXPECTED_WINDOW: "Encontrada fuera de la ventana esperada",
  REVIEW: "Revisar",
};

export const COBERTURA_CLOVER = {
  AVAILABLE: "Clover disponible",
  OUTSIDE_DATE_RANGE: "Fecha fuera del rango Clover",
  TERMINAL_NOT_AVAILABLE: "Terminal sin Clover ese día",
  RAW_DETAIL_UNAVAILABLE: "Clover sin detalle utilizable",
  AVAILABLE_NO_MATCH: "Clover disponible · sin venta original",
};

const ESTADOS_CUOTA = {
  MATCHED: "Recuperada",
  DUPLICATE_INSTALLMENT: "Duplicada · no suma",
  COUNT_MISMATCH: "Cuotas no coinciden",
  REVIEW: "Revisar",
  ORPHAN_INSTALLMENT: "Huérfana",
  OUTSIDE_CLOVER_COVERAGE: "Sin cobertura Clover",
  MULTIPLE_MOTHERS: "Varias madres",
};

const REVISAR = new Set([
  "REVIEW",
  "COUNT_MISMATCH",
  "AMOUNT_DIFFERENCE",
  "BLOCKED_MISSING_RAW_DETAIL",
  "BLOCKED_MISSING_INSTALLMENT_EVIDENCE",
]);

const TABS = [
  { id: "TODAS", label: "Todas", filtro: () => true },
  { id: "COMPLETAS", label: "Completas", filtro: (o) => o.status === "COMPLETE" },
  { id: "EN_RECUPERO", label: "En recupero", filtro: (o) => o.status === "IN_RECOVERY" },
  { id: "SIN_RECUPERO", label: "Sin recupero Fiserv", filtro: (o) => o.status === "CREATED" },
  { id: "FUERA_DE_ALCANCE", label: "Fuera de alcance Fiserv", filtro: (o) => o.status === "OUT_OF_SCOPE_RECOVERY_SOURCE" },
  { id: "REVISAR", label: "Revisar", filtro: (o) => REVISAR.has(o.status) },
  { id: "HUERFANAS", label: "Huérfanas reales", cuotas: "orphans", cuenta: (s) => s.realOrphanInstallments },
  { id: "SIN_COBERTURA", label: "Sin cobertura Clover", cuotas: "outsideCloverCoverage", cuenta: (s) => s.outsideCloverCoverageInstallments },
];

export const describirCuotas = (op) =>
  op.expectedInstallments ? `${op.receivedInstallments}/${op.expectedInstallments}` : `${op.receivedInstallments}/?`;

const importeOpcional = (valor) => (valor === null || valor === undefined ? "—" : formatearImporte(valor));
const fechas = (lista) => (lista?.length ? lista.map(formatearFecha).join(", ") : "—");

function Card({ titulo, valor, detalle, descripcion, tono }) {
  return (
    <div className={`cac-card cac-card--${tono || "neutro"}`} data-testid={`cac-card-${titulo}`}>
      <span className="cac-card__titulo">{titulo}</span>
      <strong className="cac-card__valor">{valor}</strong>
      {detalle && <span className="cac-card__detalle">{detalle}</span>}
      {descripcion && <p className="cac-card__descripcion">{descripcion}</p>}
    </div>
  );
}

function Estado({ status }) {
  const e = ESTADOS[status] || { etiqueta: status, tono: "neutro" };
  return <span className={`cac-badge cac-badge--${e.tono}`}>{e.etiqueta}</span>;
}

function SinRecupero({ op }) {
  const d = op.recoveryDiagnostic;
  if (!d) return null;
  return (
    <section className="cac-sin-recupero" data-testid={`cac-sin-recupero-${op.cloverPaymentId}`}>
      <h5>Sin recupero Fiserv</h5>
      <p className="cac-resultado">{d.result}</p>
      <dl>
        <dt>Clasificación</dt><dd>{CLASIFICACION_SIN_RECUPERO[d.classification] || d.classification}</dd>
        <dt>Primera fecha posible</dt><dd>{formatearFecha(d.firstPossibleFiservDate)}</dd>
        <dt>Ventana esperada</dt><dd>{`${formatearFecha(d.firstPossibleFiservDate)} – ${formatearFecha(d.expectedWindowEnd)}`}</dd>
        <dt>Fechas sin ZIP</dt><dd>{fechas(d.missingExpectedDates)}</dd>
        <dt>ZIPs revisados</dt><dd>{d.zipsReviewed?.length ?? 0}</dd>
        <dt>Último ZIP revisado</dt><dd>{d.lastZipReviewed || "—"} ({formatearFecha(d.lastFiservZipAvailable)})</dd>
        <dt>Días observados</dt><dd>{d.daysObservedWithoutRecovery}</dd>
      </dl>
      {d.evidence?.length > 0 && (
        <ul className="cac-evidencia">
          {d.evidence.map((e, i) => (
            <li key={`${e.zipFile}-${e.sourceFile}-${i}`}>
              {e.key}: {e.transactionTypeRaw} · lote {e.batch} · cupón {e.coupon} · {formatearImporte(e.grossAmount)} · pago {formatearFecha(e.paymentDate)}
            </li>
          ))}
        </ul>
      )}
      <p className="cac-nota">{TEXTOS.SIN_RECUPERO}</p>
    </section>
  );
}

function Detalle({ op }) {
  return (
    <tr className="cac-detalle" data-testid={`cac-detalle-${op.cloverPaymentId}`}>
      <td colSpan={9}>
        <div className="cac-detalle__grid">
          <section>
            <h5>Operación madre</h5>
            <dl>
              <dt>Venta Clover</dt><dd>{op.cloverPaymentId}</dd>
              <dt>Fecha</dt><dd>{formatearFecha(op.originalOperationDate)}</dd>
              <dt>Importe</dt><dd>{importeOpcional(op.originalSaleAmount)}</dd>
              <dt>Plan</dt><dd>{op.installmentCount ? `${op.installmentCount} cuotas` : "—"}</dd>
              <dt>Terminal</dt><dd>{op.realTerminalId || "—"}</dd>
              <dt>Lote</dt><dd>{op.batchNumber || "—"}</dd>
              <dt>Cupón</dt><dd>{op.receiptNumber || "—"}</dd>
              <dt>Last4</dt><dd>{op.last4 || "—"}</dd>
              <dt>Marca</dt><dd>{op.brand || "—"}</dd>
              <dt>Fuente de recupero</dt><dd>{FUENTE_RECUPERO[op.recoverySource] || op.recoverySource || "—"}</dd>
              <dt>Fuente cuotas</dt><dd>{op.installmentSource}</dd>
            </dl>
          </section>
          {op.status === "OUT_OF_SCOPE_RECOVERY_SOURCE" ? (
            <section className="cac-fuera-de-alcance" data-testid={`cac-fuera-de-alcance-${op.cloverPaymentId}`}>
              <h5>Fuera de alcance Fiserv</h5>
              <p>{TEXTOS.FUERA_DE_ALCANCE}</p>
            </section>
          ) : op.recoveryDiagnostic ? (
            <SinRecupero op={op} />
          ) : (
            <section className="cac-recupero">
              <h5>Recupero</h5>
              {op.installments?.length ? (
                <ul>
                  {op.installments.map((c) => (
                    <li key={c.fiservOperationId} data-testid={`cac-cuota-${c.fiservOperationId}`}>
                      <strong>
                        {c.installmentNumber ?? "?"}/{c.installmentCount ?? "?"} {formatearImporte(c.grossAmount)}
                      </strong>{" "}
                      <span className={c.countedInRecovery ? "cac-cuota--ok" : "cac-cuota--no"}>
                        {ESTADOS_CUOTA[c.matchStatus] || c.matchStatus}
                      </span>
                      <dl>
                        <dt>FiservOperationId</dt><dd>{c.fiservOperationId}</dd>
                        <dt>Fecha pago</dt><dd>{formatearFecha(c.fiservPaymentDate)}</dd>
                        <dt>Bruto</dt><dd>{formatearImporte(c.grossAmount)}</dd>
                        <dt>Arancel</dt><dd>{importeOpcional(c.dtoArancel)}</dd>
                        <dt>Dto. financiero</dt><dd>{importeOpcional(c.dtoFinanciero)}</dd>
                        <dt>Liquidación</dt><dd>{c.fiservLiquidationNumber || "—"}</dd>
                        <dt>Source</dt><dd>{`${c.sourceFile} · p${c.sourcePage} · fila ${c.sourceRow}`}</dd>
                        {c.warnings?.length > 0 && (<><dt>Warnings</dt><dd>{c.warnings.join(", ")}</dd></>)}
                      </dl>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cac-vacio">Todavía no llegó ninguna cuota Fiserv.</p>
              )}
            </section>
          )}
          <section className="cac-detalle__warnings">
            <h5>Warnings</h5>
            {op.warnings?.length ? (
              <ul>{op.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            ) : (
              <p className="cac-vacio">Sin warnings</p>
            )}
          </section>
        </div>
      </td>
    </tr>
  );
}

function TablaCuotas({ filas, total, texto, testid }) {
  if (!filas.length) return <p className="cac-vacio">No hay cuotas en esta vista.</p>;
  return (
    <>
      <p className="cac-nota">{texto}</p>
      {total > filas.length && (
        <p className="cac-nota">Se muestran {filas.length} de {total} cuotas.</p>
      )}
      <table className="cac-tabla" data-testid={testid}>
        <thead>
          <tr>
            <th>Identidad</th>
            <th>Cuota</th>
            <th>Bruto</th>
            <th>Fecha venta</th>
            <th>Fecha pago</th>
            <th>Cobertura Clover</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((c) => (
            <tr key={c.fiservOperationId}>
              <td>{c.financingIdentityKey}</td>
              <td>{c.installmentNumber ?? "?"}/{c.installmentCount ?? "?"}</td>
              <td>{formatearImporte(c.grossAmount)}</td>
              <td>{formatearFecha(c.originalOperationDate)}</td>
              <td>{formatearFecha(c.fiservPaymentDate)}</td>
              <td>{COBERTURA_CLOVER[c.cloverCoverageStatus] || c.cloverCoverageStatus}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function DisponibilidadFiserv({ disponibilidad }) {
  if (!disponibilidad?.zips?.length) return null;
  return (
    <div className="cac-disponibilidad" data-testid="cac-disponibilidad">
      <span>
        ZIP Fiserv revisados: <strong>{disponibilidad.zips.length}</strong> · pagos {fechas(disponibilidad.availablePaymentDates)}
      </span>
      <span data-testid="cac-zip-faltantes">
        Días hábiles sin ZIP: <strong>{fechas(disponibilidad.missingBusinessDates)}</strong>
      </span>
    </div>
  );
}

export default function CuotaACuota({ runId, token, darkMode }) {
  const [datos, setDatos] = useState(null),
    [cargando, setCargando] = useState(false),
    [error, setError] = useState(""),
    [tab, setTab] = useState("TODAS"),
    [colapsado, setColapsado] = useState(false),
    [abierta, setAbierta] = useState("");

  const cargar = useCallback(async () => {
    if (!runId) return;
    setCargando(true);
    setError("");
    try {
      setDatos(await api.obtenerCuotaACuota(runId, token));
    } catch (e) {
      setDatos(null);
      setError(e?.message || "No se pudo cargar Cuota a cuota.");
    } finally {
      setCargando(false);
    }
  }, [runId, token]);

  useEffect(() => {
    setDatos(null);
    setTab("TODAS");
    setAbierta("");
    cargar();
  }, [cargar]);

  const operaciones = useMemo(() => datos?.operations || [], [datos]);
  const tabActual = TABS.find((t) => t.id === tab) || TABS[0];
  const visibles = useMemo(
    () => (tabActual.cuotas ? [] : operaciones.filter(tabActual.filtro)),
    [operaciones, tabActual]
  );
  const s = datos?.summary;

  if (!runId) return null;

  const revisar = s ? s.review + s.countMismatch + s.amountDifferences + s.blockedMissingRaw : 0;
  const capitalSinRecupero = (s?.createdWithoutFiservByClassification || []).reduce((t, g) => t + g.capital, 0);

  return (
    <section className={`cac ${darkMode ? "cac--dark" : ""}`} data-testid="cuota-a-cuota">
      <header className="cac__header">
        <h3>Cuota a cuota Zoco</h3>
        <p className="cac-nota">{NOTA_SIMULACION}</p>
        <button
          type="button"
          className="cac__toggle"
          onClick={() => setColapsado((v) => !v)}
          data-testid="cac-toggle"
          aria-expanded={!colapsado}
        >
          {colapsado ? "Mostrar detalle" : "Ocultar detalle"}
        </button>
        <button type="button" onClick={cargar} disabled={cargando}>
          {cargando ? "Cargando…" : "Actualizar"}
        </button>
      </header>

      {error && (
        <p className="cac-error" role="alert" data-testid="cac-error">
          {error}
        </p>
      )}

      {cargando && !datos && <p data-testid="cac-cargando">Cargando cuota a cuota…</p>}

      {!cargando && !error && datos && s && (
        <>
          {datos.limitations?.length > 0 && (
            <ul className="cac-limitaciones" data-testid="cac-limitaciones">
              {datos.limitations.map((l) => <li key={l}>{l}</li>)}
            </ul>
          )}

          <div className="cac-cards">
            <Card titulo="Capital financiado" valor={formatearImporte(s.capitalOriginal)} detalle={`${s.financedMothers} operaciones madre`} />
            <Card titulo="Recuperado Fiserv" valor={formatearImporte(s.capitalFiservRecovered)} detalle={`${s.matchedInstallments} cuotas`} tono="verde" />
            <Card titulo="Pendiente Fiserv" valor={formatearImporte(s.capitalFiservPending)} tono="azul" />
            <Card titulo="Operaciones completas" valor={s.complete} tono="verde" />
            <Card titulo="Revisar" valor={revisar} detalle={`${s.blockedMissingRaw} bloqueadas por falta de raw`} tono="naranja" />
          </div>

          <div className="cac-cards cac-cards--cobertura">
            <Card
              titulo="En recupero Fiserv"
              valor={s.fiservMothers}
              detalle={`madres · ${formatearImporte(s.capitalFiservRecoverable)} · recuperado ${formatearImporte(s.capitalFiservRecovered)}`}
              tono="azul"
            />
            <Card
              titulo="Fuera de alcance Fiserv"
              valor={s.outOfScopeRecoverySource}
              detalle={`madres · ${formatearImporte(s.capitalNonFiserv)}`}
              descripcion={TEXTOS.FUERA_DE_ALCANCE}
            />
            <Card
              titulo="Posible ZIP faltante"
              valor={s.possibleMissingZipMothers}
              detalle={`madres · ${formatearImporte(s.capitalPendingMissingZip)} pendiente`}
              descripcion={TEXTOS.ZIP_FALTANTE}
              tono="naranja"
            />
            <Card
              titulo="Huérfanas reales"
              valor={s.realOrphanInstallments}
              detalle={`cuotas · ${s.realOrphanFinancings} financiaciones`}
              descripcion={TEXTOS.HUERFANA}
              tono={s.realOrphanInstallments > 0 ? "rojo" : "verde"}
            />
            <Card
              titulo="Sin cobertura Clover"
              valor={s.outsideCloverCoverageInstallments}
              detalle={`cuotas · ${s.outsideCloverCoverageFinancings} financiaciones`}
              descripcion={TEXTOS.SIN_COBERTURA}
            />
          </div>

          {s.createdWithoutFiserv > 0 && (
            <p className="cac-nota" data-testid="cac-sin-recupero">
              Sin recupero Fiserv: <strong>{s.createdWithoutFiserv}</strong> madres · {formatearImporte(capitalSinRecupero)}. {TEXTOS.SIN_RECUPERO}
            </p>
          )}

          {s.createdWithoutFiservByClassification?.length > 0 && (
            <ul className="cac-clasificacion" data-testid="cac-clasificacion">
              {s.createdWithoutFiservByClassification.map((g) => (
                <li key={g.classification}>
                  {CLASIFICACION_SIN_RECUPERO[g.classification] || g.classification}: <strong>{g.mothers}</strong> · {formatearImporte(g.capital)}
                </li>
              ))}
            </ul>
          )}

          <DisponibilidadFiserv disponibilidad={datos.fiservAvailability} />

          <p className="cac-contadores" data-testid="cac-contadores">
            Duplicadas: <strong>{s.duplicateInstallments}</strong>
            · Varias madres: <strong>{s.multipleMothers}</strong>
            · Diferencias de importe: <strong>{s.amountDifferences}</strong>
            · Devol./Contr. en cuotas: <strong>{s.installmentReversals}</strong>
            {s.dateToleranceMatches > 0 && <> · Match con fecha ±1 día: <strong>{s.dateToleranceMatches}</strong></>}
          </p>

          {!colapsado && (
            <nav className="cac-tabs" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={tab === t.id ? "activo" : ""}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}{" "}
                  <span className="cac-tabs__cuenta">{t.cuotas ? t.cuenta(s) : operaciones.filter(t.filtro).length}</span>
                </button>
              ))}
            </nav>
          )}

          {colapsado ? null : tabActual.cuotas ? (
            <TablaCuotas
              filas={datos[tabActual.cuotas] || []}
              total={tabActual.cuenta(s)}
              texto={tabActual.id === "HUERFANAS" ? TEXTOS.HUERFANA : TEXTOS.SIN_COBERTURA}
              testid={`cac-tabla-${tabActual.id.toLowerCase()}`}
            />
          ) : visibles.length === 0 ? (
            <p className="cac-vacio" data-testid="cac-sin-resultados">
              No hay operaciones en esta vista.
            </p>
          ) : (
            <table className="cac-tabla">
              <thead>
                <tr>
                  <th>Comercio</th>
                  <th>Fecha venta</th>
                  <th>Clover</th>
                  <th>Plan</th>
                  <th>Venta original</th>
                  <th>Cuotas recibidas</th>
                  <th>Recuperado</th>
                  <th>Pendiente</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((op) => (
                  <React.Fragment key={op.cloverPaymentId}>
                    <tr
                      className="cac-fila"
                      onClick={() => setAbierta(abierta === op.cloverPaymentId ? "" : op.cloverPaymentId)}
                      data-testid={`cac-fila-${op.cloverPaymentId}`}
                    >
                      <td>{op.commerceName || op.merchantId || "—"}</td>
                      <td>{formatearFecha(op.originalOperationDate)}</td>
                      <td>{op.cloverPaymentId}</td>
                      <td>{op.installmentCount ? `${op.installmentCount} cuotas` : "—"}</td>
                      <td>{importeOpcional(op.originalSaleAmount)}</td>
                      <td>{describirCuotas(op)}</td>
                      <td>{formatearImporte(op.amountRecovered)}</td>
                      <td>{importeOpcional(op.pendingAmount)}</td>
                      <td>
                        <Estado status={op.status} />
                        {op.recoveryDiagnostic && (
                          <span className="cac-clasificacion__fila">
                            {CLASIFICACION_SIN_RECUPERO[op.recoveryDiagnostic.classification] || op.recoveryDiagnostic.classification}
                          </span>
                        )}
                      </td>
                    </tr>
                    {abierta === op.cloverPaymentId && <Detalle op={op} />}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
